import { useMemo } from "react";
import { Activity, ActivityData } from "@/entities/activities/types";
import { getRelativeTime, toCalendarDate } from "@/utils/datetime";

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const useSplitActivityByTimes = (
  activities: ActivityData[],
  includedTypes: Activity[] = []
) => {
  const upcomingActivities = useMemo(() => {
    const now = new Date();
    const oneYearAhead = new Date(now.getTime() + 365 * MS_PER_DAY);

    const filteredActivities = activities.filter((activity) => {
      if (!activity?.notes?.date || activity.type === undefined) return false;
      if (activity.notes?.isCancelled) return false;
      if (
        includedTypes.length > 0 &&
        !includedTypes.includes(activity.type as Activity)
      )
        return false;
      return true;
    });

    const recurringActivities: ActivityData[] = [];
    const nonRecurringActivities: ActivityData[] = [];

    filteredActivities.forEach((activity) => {
      const activityDate = toCalendarDate(activity.notes!.date);

      if (activity.notes?.isRecurring) {
        if (activityDate > now && activityDate <= oneYearAhead) {
          recurringActivities.push(activity);
        }
      } else {
        if (activityDate > now && activityDate <= oneYearAhead) {
          nonRecurringActivities.push(activity);
        }
      }
    });

    // Group recurring activities by series and pick nearest occurrence
    const recurringGroups = new Map<string, ActivityData[]>();
    recurringActivities.forEach((activity) => {
      const recurringEventId = activity.notes!.recurringEventId!;
      if (!recurringGroups.has(recurringEventId)) {
        recurringGroups.set(recurringEventId, []);
      }
      recurringGroups.get(recurringEventId)!.push(activity);
    });

    // For each recurring group, pick the nearest occurrence
    const nearestRecurringEvents: ActivityData[] = [];
    recurringGroups.forEach((groupEvents) => {
      const nearest = groupEvents.reduce((earliest, current) => {
        const earliestDate = toCalendarDate(earliest.notes!.date);
        const currentDate = toCalendarDate(current.notes!.date);
        return currentDate < earliestDate ? current : earliest;
      });
      nearestRecurringEvents.push(nearest);
    });

    const result = [...nonRecurringActivities, ...nearestRecurringEvents];

    const sortedResult = result.sort((a, b) => {
      const dateA = toCalendarDate(a.notes!.date);
      const dateB = toCalendarDate(b.notes!.date);
      return dateA.getTime() - dateB.getTime();
    });

    return sortedResult.map((activity) => ({
      ...activity,
      formattedDistance: getRelativeTime(toCalendarDate(activity.notes!.date))
    }));
  }, [activities, includedTypes]);

  const pastActivities = useMemo(() => {
    const now = new Date();
    return activities.filter((activity) => {
      if (!activity?.notes?.date || activity.type === undefined) return false;
      if (
        includedTypes.length > 0 &&
        !includedTypes.includes(activity.type as Activity)
      )
        return false;
      const activityDate = toCalendarDate(activity.notes.date);
      return activityDate <= now;
    });
  }, [activities, includedTypes]);

  return { upcomingActivities, pastActivities };
};
